// ChapterNav — minimal right-rail chapter indicator. Seven dots, one
// per scene, with hover tooltips and click-to-scroll. Hidden on mobile.
// Each dot is wrapped in a Magnetic component so it gets the 100px
// cursor-magnet effect, the hover scale spring, and the click squish.

import { useCallback, useEffect, useState } from 'react';
import { SCENES, type SceneConfig } from './planetSections';
import { Magnetic } from './Magnetic';
import { onChapterNavClick } from '../utils/analytics';

// ---------------------------------------------------------------------------
// Hook — track the active scene index from the global scroll timeline.
// ---------------------------------------------------------------------------

function useActiveChapter(): number {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    setIdx(window.__planetSceneIndex ?? 0);
    let last = window.__planetSceneIndex ?? 0;
    const id = window.setInterval(() => {
      const cur = window.__planetSceneIndex ?? 0;
      if (cur !== last) {
        last = cur;
        setIdx(cur);
      }
    }, 200);
    return () => window.clearInterval(id);
  }, []);
  return idx;
}

// ---------------------------------------------------------------------------
// scrollToProgress — given a target 0..1, scroll the page so the
// document's scrollY lands at that fraction of the total scroll range.
// Uses smooth scrolling if the browser supports it.
// ---------------------------------------------------------------------------

function scrollToProgress(target: number) {
  if (typeof window === 'undefined') return;
  const max = document.documentElement.scrollHeight - window.innerHeight;
  if (max <= 0) return;
  const y = Math.max(0, Math.min(max, target * max));
  window.scrollTo({ top: y, behavior: 'smooth' });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ChapterNav() {
  const active = useActiveChapter();

  const onDotClick = useCallback((scene: SceneConfig) => {
    // Scroll to the middle of the scene's range for a sensible anchor.
    const target = (scene.start + scene.end) / 2;
    scrollToProgress(target);
    onChapterNavClick(scene.key);
  }, []);

  return (
    <nav className="chapter-nav" aria-label="Chapters">
      <div role="tablist" aria-label="Chapter selector" className="chapter-nav__list">
        {SCENES.map((scene) => {
          const isActive = active === scene.id;
          return (
            <Magnetic
              key={scene.key}
              as="button"
              role="tab"
              aria-selected={isActive}
              aria-controls={`scene-${scene.key}`}
              tabIndex={isActive ? 0 : -1}
              className={[
                'chapter-nav__dot',
                isActive ? 'is-active' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              ariaLabel={`Go to chapter ${scene.id + 1}: ${scene.name}`}
              onClick={() => onDotClick(scene)}
              innerClassName="chapter-nav__dot-inner"
              type="button"
            >
              <span className="chapter-nav__tooltip">{scene.name}</span>
            </Magnetic>
          );
        })}
      </div>
    </nav>
  );
}
